import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { getSession, signOut, onAuthStateChange } from '@/lib/api';

interface SessionTimeoutConfig {
  /** Idle timeout in milliseconds (default: 30 minutes) */
  idleTimeout?: number;
  /** Absolute timeout in milliseconds (default: 8 hours) */
  absoluteTimeout?: number;
  /** Show warning before timeout (in milliseconds before timeout) */
  warningTime?: number;
  /** Enable session timeout (default: true) */
  enabled?: boolean;
}

const DEFAULT_CONFIG: Required<SessionTimeoutConfig> = {
  idleTimeout: 30 * 60 * 1000, // 30 minutes
  absoluteTimeout: 8 * 60 * 60 * 1000, // 8 hours
  warningTime: 5 * 60 * 1000, // 5 minutes
  enabled: true,
};

/**
 * Hook to manage session timeout and idle detection
 * Automatically signs out users after period of inactivity or absolute timeout
 */
export const useSessionTimeout = (config: SessionTimeoutConfig = {}) => {
  const { t } = useTranslation();
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const navigate = useNavigate();
  const { toast } = useToast();

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const absoluteTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionStartRef = useRef<number>(Date.now());
  const warningShownRef = useRef(false);

  const handleSignOut = useCallback(
    async (reason: 'idle' | 'absolute') => {
      if (!finalConfig.enabled) return;

      try {
        await signOut();

        toast({
          title: t('common.usesessiontimeout.session_expired', 'Session Expired'),
          description:
            reason === 'idle'
              ? t('common.usesessiontimeout.you_were_signed', 'You were signed out due to inactivity.')
              : t('common.usesessiontimeout.your_session_has', 'Your session has expired. Please sign in again.'),
          variant: 'destructive',
        });

        navigate('/auth');
      } catch (error) {
        console.error('Error signing out:', error);
      }
    },
    [finalConfig.enabled, navigate, toast]
  );

  const showWarning = useCallback(() => {
    if (warningShownRef.current) return;

    warningShownRef.current = true;
    toast({
      title: t('common.usesessiontimeout.session_expiring_soon', 'Session Expiring Soon'),
      description: `Your session will expire in ${Math.floor(finalConfig.warningTime / 60000)} minutes due to inactivity.`,
    });
  }, [finalConfig.warningTime, toast]);

  const resetIdleTimer = useCallback(() => {
    if (!finalConfig.enabled) return;

    // Clear existing timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    warningShownRef.current = false;

    // Set warning timer
    if (finalConfig.warningTime > 0) {
      const warningTimeout = finalConfig.idleTimeout - finalConfig.warningTime;
      warningTimerRef.current = setTimeout(showWarning, warningTimeout);
    }

    // Set idle timeout
    idleTimerRef.current = setTimeout(() => {
      handleSignOut('idle');
    }, finalConfig.idleTimeout);
  }, [
    finalConfig.enabled,
    finalConfig.idleTimeout,
    finalConfig.warningTime,
    handleSignOut,
    showWarning,
  ]);

  const resetAbsoluteTimer = useCallback(() => {
    if (!finalConfig.enabled) return;

    sessionStartRef.current = Date.now();

    if (absoluteTimerRef.current) {
      clearTimeout(absoluteTimerRef.current);
    }

    absoluteTimerRef.current = setTimeout(() => {
      handleSignOut('absolute');
    }, finalConfig.absoluteTimeout);
  }, [finalConfig.enabled, finalConfig.absoluteTimeout, handleSignOut]);

  useEffect(() => {
    if (!finalConfig.enabled) return;

    // Check if user is logged in
    getSession().then((result) => {
      if (!result.success || !result.data) return;

      // Initialize timers
      resetIdleTimer();
      resetAbsoluteTimer();

      // Activity events to track
      const events = [
        'mousedown',
        'mousemove',
        'keypress',
        'scroll',
        'touchstart',
        'click',
      ];

      // Reset idle timer on user activity
      events.forEach((event) => {
        document.addEventListener(event, resetIdleTimer, { passive: true });
      });

      // Cleanup
      return () => {
        events.forEach((event) => {
          document.removeEventListener(event, resetIdleTimer);
        });

        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
        }
        if (absoluteTimerRef.current) {
          clearTimeout(absoluteTimerRef.current);
        }
        if (warningTimerRef.current) {
          clearTimeout(warningTimerRef.current);
        }
      };
    });
  }, [finalConfig.enabled, resetIdleTimer, resetAbsoluteTimer]);

  // Monitor auth state changes
  useEffect(() => {
    const subscription = onAuthStateChange((session) => {
      if (!session) {
        // Clear all timers on sign out
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        if (absoluteTimerRef.current) clearTimeout(absoluteTimerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      } else {
        // Reset timers on sign in
        resetIdleTimer();
        resetAbsoluteTimer();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [resetIdleTimer, resetAbsoluteTimer]);

  return {
    resetIdleTimer,
    resetAbsoluteTimer,
  };
};
